import React, { useMemo } from "react";
import { Button, Spinner } from "reactstrap";
import Select from "react-select";
import { FaFileUpload, FaTimes } from "react-icons/fa";

import { AUTOPILOT_COLORS } from "../../../../components/autopilot/styles";
import SectionCard from "../../../../components/SectionCard";
import { MAX_ARQUIVOS, extensaoDoTipo, formatBytes } from "../helpers";

// Fluxo de envio em 2 passos (PO1-2717): "1. Tipo de informação" e
// "2. Arquivos". Cada passo é um SectionCard, então o usuário vê o que falta
// antes de conseguir enviar. Sem modal.
export default function FormularioEnvio({
  cfg,
  tipos,
  loadingTipos,
  selectedTipo,
  onSelectTipo,
  arquivos,
  onFilesChosen,
  onRemoveArquivo,
  notificarEmail,
  onNotificarEmail,
  userEmail,
  enviando,
  onEnviar,
}) {
  const acceptExtensao = useMemo(() => {
    const ext = extensaoDoTipo(selectedTipo?.label);
    return ext ? `${ext},.zip` : ".zip";
  }, [selectedTipo]);

  const totalBytes = useMemo(
    () => arquivos.reduce((acc, file) => acc + file.size, 0),
    [arquivos]
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
      <SectionCard
        eyebrow="Passo 1"
        title="Tipo de informação"
        description="Define a pasta de destino e a extensão aceita."
      >
        {loadingTipos ? (
          <div style={{ textAlign: "center", padding: "1rem" }}>
            <Spinner color="info" size="sm" />
          </div>
        ) : (
          <Select
            className="react-select info"
            classNamePrefix="react-select"
            name="selectTipo"
            value={selectedTipo}
            onChange={onSelectTipo}
            options={tipos}
            placeholder="Tipo de informação"
          />
        )}
      </SectionCard>

      <SectionCard
        eyebrow="Passo 2"
        title="Arquivos"
        description={`Até ${MAX_ARQUIVOS} arquivos e 10 MB por envio. Acima disso, envie um .zip.`}
      >
        <label
          htmlFor={cfg.inputId}
          style={{
            display: "block",
            border: `2px dashed ${AUTOPILOT_COLORS.accent}`,
            borderRadius: 14,
            padding: "1.5rem",
            textAlign: "center",
            cursor: selectedTipo ? "pointer" : "not-allowed",
            opacity: selectedTipo ? 1 : 0.5,
            color: AUTOPILOT_COLORS.muted,
            marginBottom: 14,
            background: `color-mix(in srgb, ${AUTOPILOT_COLORS.accent} 6%, transparent)`,
          }}
        >
          <FaFileUpload size={22} />
          <div style={{ marginTop: 8, fontSize: "0.84rem" }}>
            {selectedTipo
              ? "Clique para selecionar os arquivos (ou .zip)"
              : "Selecione o tipo de informação primeiro"}
          </div>
          <input
            id={cfg.inputId}
            type="file"
            multiple
            disabled={!selectedTipo}
            accept={acceptExtensao}
            onChange={onFilesChosen}
            style={{ display: "none" }}
          />
        </label>

        {arquivos.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 8,
                color: AUTOPILOT_COLORS.muted,
                fontSize: "0.76rem",
              }}
            >
              <span>
                {arquivos.length}/{MAX_ARQUIVOS} arquivos
              </span>
              <span>{formatBytes(totalBytes)} / 10 MB</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {arquivos.map((file, index) => (
                <div
                  key={`${file.name}-${file.size}-${index}`}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 10px",
                    border: `1px solid ${AUTOPILOT_COLORS.border}`,
                    borderRadius: 8,
                  }}
                >
                  <span
                    style={{
                      color: AUTOPILOT_COLORS.text,
                      fontSize: "0.8rem",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {file.name}
                  </span>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      flexShrink: 0,
                    }}
                  >
                    <span style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.74rem" }}>
                      {formatBytes(file.size)}
                    </span>
                    <button
                      type="button"
                      aria-label={`Remover ${file.name}`}
                      onClick={() => onRemoveArquivo(index)}
                      style={{
                        border: "none",
                        background: "transparent",
                        color: AUTOPILOT_COLORS.statusDanger,
                        cursor: "pointer",
                      }}
                    >
                      <FaTimes size={12} />
                    </button>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 14,
            fontSize: "0.8rem",
            color: AUTOPILOT_COLORS.text,
          }}
        >
          <input
            id={cfg.checkboxId}
            type="checkbox"
            checked={notificarEmail}
            onChange={(e) => onNotificarEmail(e.target.checked)}
          />
          <label htmlFor={cfg.checkboxId} style={{ marginBottom: 0 }}>
            Avisar por e-mail quando o processamento terminar
            {userEmail ? ` (${userEmail})` : ""}
          </label>
        </div>

        <Button
          className="btn btn-round btn-success"
          onClick={onEnviar}
          disabled={!arquivos.length || !selectedTipo || enviando}
        >
          {enviando ? (
            <>
              <Spinner size="sm" /> Enviando...
            </>
          ) : (
            <>
              <FaFileUpload size={16} /> {"  "}Enviar
            </>
          )}
        </Button>
      </SectionCard>
    </div>
  );
}
